import type { Guide } from "./guide";

/** Guide 4: Bash inside your app, from a session your server opens: embed, watch, talk, and ending one. */
export const INSIDE_YOUR_APP: Guide = {
  slug: "inside-your-app",
  samples: "app",
  title: "Put Bash inside your app",
  nav: "Inside your app",
  summary:
    "Your signed-in user talks to Bash in your own page: chat, approvals, its screen and voice, from a session your server opens.",
  minutes: 10,
  lede: "Your user talks to Bash without leaving your app. Your server opens a session for them, and your page shows it.",
  lines: ["embed", "watch", "talk"],
  steps: [
    {
      id: "open",
      title: "Open a session",
      body: [
        "On your server, for the user signed in to your app. Its token lasts 15 minutes and reaches only that user ([`embed`](/developers/embed)).",
      ],
      doors: {
        ts: { sample: "guide-app-open" },
        py: { sample: "guide-app-open" },
        cli: { sample: "guide-app-open" },
        http: { sample: "guide-app-open" },
        mcp: { sample: "guide-app-open" },
      },
      then: "Bash answers with a `url` for this user, and `session.started` reaches your webhooks.",
    },
    {
      id: "frame",
      title: "Show it in your page",
      body: [
        'Put the `url` in an iframe with `allow="microphone"`, so voice works. Name the pages that may frame it in `allowed_embed_origins`, with `PATCH /v1/org`.',
      ],
      file: "embed.html",
      then: "Your user chats, approves in place and watches Bash work, with no Vestra sign-in.",
    },
    {
      id: "watch",
      title: "Or show just its screen",
      body: [
        "For the screen alone, in a tab or a support console, opened in the user's conversation. With control, a person can take the wheel and give it back ([`watch`](/developers/watch)).",
      ],
      doors: {
        ts: { sample: "guide-app-watch" },
        py: { sample: "guide-app-watch" },
        cli: { sample: "guide-app-watch" },
        http: { sample: "guide-app-watch" },
        mcp: { sample: "guide-app-watch" },
      },
      then: "The page shows Bash's computer, live, and `screen.control` on the conversation's stream says who is driving.",
    },
    {
      id: "talk",
      title: "Or start a voice call",
      body: ["A call in the user's conversation ([`talk`](/developers/talk))."],
      doors: {
        ts: { sample: "guide-app-talk" },
        py: { sample: "guide-app-talk" },
        cli: { sample: "guide-app-talk" },
        http: { sample: "guide-app-talk" },
        mcp: { sample: "talk" },
      },
      then: "The page joins the call, and what is said lands in the conversation as text. When it ends, `session.ended` carries the transcript.",
    },
    {
      id: "end",
      title: "End it",
      body: ["When your user signs out, end their session by its id."],
      doors: {
        ts: { sample: "guide-app-end" },
        py: { sample: "guide-app-end" },
        cli: {
          note: "The CLI's sessions are previews, good for 15 minutes. End one from your server, with an SDK or HTTP.",
        },
        http: { sample: "guide-app-end" },
        mcp: { note: "Sessions end from your server, with an SDK or HTTP." },
      },
      then: "Its token stops working at once, its streams close, and `session.ended` says so.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: ["Open, show, call and end, in one program."],
      doors: {
        ts: { sample: "guide-app-whole" },
        py: { sample: "guide-app-whole" },
        cli: { sample: "guide-app-whole" },
        http: { sample: "guide-app-whole" },
        mcp: {
          note: "An agent opens each with the `embed`, `watch` and `talk` tools. Ending one stays with your server.",
        },
      },
      then: "One session per visit, and your user never leaves your app.",
    },
  ],
};
